import { useMemo } from 'react';
import { useCurrentOrderContext } from '..';
import { Breakfast, Order, OrderModule } from '../../../infrastructure/apis/orderando';
import { calculateTotalPrice } from './price-calculation';

export function useNumOrderItems() {
  const { currentOrder } = useCurrentOrderContext();

  const numProductsInOrder = currentOrder.order.menues
    .map((menu) => menu.extras.map((extra) => extra.quantity))
    .flat()
    .reduce((sum, num) => sum + num, 0);

  return numProductsInOrder;
}

export function useActiveOrderLocation() {
  const { currentOrder } = useCurrentOrderContext();

  const activeLocation =
    currentOrder.orderLocations && currentOrder.deliveryLocationId
      ? currentOrder.orderLocations.find(
          (location) => location._id === currentOrder.deliveryLocationId
        )
      : undefined;

  return activeLocation;
}

export function formatPrice(cost: number, locale = 'de-DE') {
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: 'EUR',
  }).format(cost);
}

export function useOrderTotalCost() {
  const { currentOrder } = useCurrentOrderContext();
  const activeLocation = useActiveOrderLocation();

  return calculateTotalPrice({ currentOrder, activeLocation });
}

interface LocationElement {
  locationId?: string;
  sublocationId?: string;
  parentLocationId?: string;
}

/**
 * Finds a location or sublocation by ID
 */
export function useFindLocationById(locationOrSublocationId?: string) {
  const { currentOrder } = useCurrentOrderContext();

  return useMemo(() => {
    if (!currentOrder.orderLocations || !locationOrSublocationId) return undefined;

    const allLocations = currentOrder.orderLocations
      .map((location) => {
        return {
          locationId: location._id,
          sublocations: location.sublocations.map((subloc) => ({
            sublocationId: subloc._id,
            parentLocationId: location._id,
          })),
        };
      })
      .reduce((allLocations, currentLocation) => {
        return [
          ...allLocations,
          { locationId: currentLocation.locationId },
          ...currentLocation.sublocations,
        ];
      }, [] as LocationElement[]);
    const foundLocation = allLocations.find(
      (location) =>
        location.locationId === locationOrSublocationId ||
        location.sublocationId === locationOrSublocationId
    );

    return foundLocation;
  }, [locationOrSublocationId, currentOrder.orderLocations]);
}

export function isOrderModule(
  currentModule: OrderModule | undefined,
  activeModule: Breakfast | Order | undefined
): activeModule is Order {
  return currentModule === 'order';
}
